// src/pages/OperatorSignIn.tsx
//
// Sign-in for the platform operator portal. Different domain, no tenant, no
// branding — and 2FA is mandatory rather than encouraged, because this login
// reaches every tenant database.

import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  ArrowPathIcon,
  ClockIcon,
  KeyIcon,
  LockClosedIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { signIn, twoFactor, passkeysReady, setOperatorToken } from "../auth/betterAuthClient";
import {
  clearPending,
  formatRemaining,
  isChallengeExpired,
  readPending,
  remainingMs,
  savePending,
  type PendingSignIn,
} from "../auth/pendingSignIn";

/**
 * Namespaces this portal's pending-sign-in state apart from the tenant
 * portal's. The code step survives a refresh for as long as the server's signed
 * challenge cookie does (see auth/pendingSignIn.ts) — but there is deliberately
 * NO "trust this device" here: the operator instance refuses it server-side.
 */
const PENDING_SCOPE = "operator";

export default function OperatorSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"credentials" | "totp">("credentials");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingSignIn | null>(null);
  const [resumed, setResumed] = useState(false);
  const [expiredNotice, setExpiredNotice] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const codeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Restore a code step interrupted by a refresh.
  useEffect(() => {
    const state = readPending(PENDING_SCOPE);
    if (state.status === "active") {
      setEmail(state.pending.email);
      setPending(state.pending);
      setStage("totp");
      setResumed(true);
    } else if (state.status === "expired") {
      setEmail(state.email);
      setExpiredNotice(true);
    }
  }, []);

  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [pending]);

  const left = pending ? remainingMs(pending, now) : 0;

  useEffect(() => {
    if (pending && left === 0) expireChallenge();
  }, [pending, left]);

  useEffect(() => {
    if (stage === "totp") codeRef.current?.focus();
  }, [stage]);
  useEffect(() => {
    if (expiredNotice) passwordRef.current?.focus();
  }, [expiredNotice]);

  function expireChallenge() {
    clearPending(PENDING_SCOPE);
    setPending(null);
    setStage("credentials");
    setResumed(false);
    setCode("");
    setPassword("");
    setError(null);
    setExpiredNotice(true);
  }

  function startOver() {
    clearPending(PENDING_SCOPE);
    setPending(null);
    setStage("credentials");
    setResumed(false);
    setCode("");
    setPassword("");
    setError(null);
    setExpiredNotice(false);
  }

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Drop any leftover token BEFORE signing in.
      //
      // The bearer() plugin means the server resolves a session from the
      // Authorization header first, and this client attaches whatever is in
      // sessionStorage to every request — including verify-totp. A token left
      // over from an earlier sign-in therefore sends the two-factor check down
      // the "already has a session" branch instead of the cookie branch it is
      // actually in the middle of, for an identity that may not even match the
      // email just typed.
      setOperatorToken(null);

      const res = await signIn.email({ email, password });

      if (res.error) {
        setError(res.error.message || "Could not sign in.");
        return;
      }

      // "2FA required" arrives as a SUCCESS, not an error.
      //
      // better-auth's twoFactor plugin answers a correct password with
      // { twoFactorRedirect: true, twoFactorMethods: [...] } in `data`, and
      // leaves the session half-established until the code is verified. An
      // earlier version only looked for an error code here, so this branch
      // never ran: the moment an operator actually enrolled, sign-in fell
      // through to the no-2FA path below, redirected on a session that
      // getSession() still reported as null, and RequireAuth bounced them
      // straight back to this page. Enrolling 2FA locked you out.
      const data = res.data as { twoFactorRedirect?: boolean } | undefined;
      if (data?.twoFactorRedirect) {
        const started = Date.now();
        savePending(PENDING_SCOPE, email, started);
        setPending({ email, startedAt: started });
        setNow(started);
        setResumed(false);
        setExpiredNotice(false);
        setStage("totp");
        return;
      }

      // No second factor on the account. Let them in and take them straight to
      // enrolment rather than refusing — enrolling requires a session, so
      // refusing the sign-in is a deadlock. Every /super-admin route keeps
      // returning 403 two_factor_required until they finish, which is what
      // OperatorAuthProvider reads to route them here.
      window.location.assign("/security/setup");
    } catch {
      setError("Could not reach the API. Check VITE_API_URL and your connection.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Passwordless sign-in.
   *
   * A passkey already proves possession of a device, so Better Auth issues a
   * full session without a separate TOTP step. Only offered where the portal's
   * domain is final — a credential minted on a hostname we are leaving is dead
   * after the move, with nothing to migrate.
   */
  async function signInWithPasskey() {
    setBusy(true);
    setError(null);
    try {
      const res = await signIn.passkey();
      if (res?.error) {
        setError(res.error.message || "That passkey was not accepted.");
        return;
      }
      window.location.assign("/");
    } catch {
      // Dismissing the browser prompt lands here — a choice, not a failure.
      setError(null);
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // No trustDevice — ever. The operator instance rejects it server-side
      // (TRUST_DEVICE_NOT_ALLOWED); sending it would only turn a valid code
      // into an error.
      const res = await twoFactor.verifyTotp({ code });
      if (res.error) {
        if (isChallengeExpired(res.error)) {
          expireChallenge();
          return;
        }
        setError(res.error.message || "That code was not accepted.");
        return;
      }
      clearPending(PENDING_SCOPE);
      window.location.assign("/");
    } catch {
      setError("Could not verify the code. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    // DELIBERATELY NOT THE TENANT PORTAL'S LOOK.
    //
    // This is the one surface that reaches every tenant database, and it used
    // to be a white card that could pass for any customer's login. Making the
    // two indistinguishable invites the mistake worth preventing: acting here
    // while believing you are in a single company's portal. Dark ground,
    // monospace labels and no tenant branding of any kind, so the difference
    // registers before you read a word.
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0C1116] px-6 py-12">
      {/* Faint engineering grid — texture, not decoration: it reads as a console. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.028) 0 1px, transparent 1px 64px), repeating-linear-gradient(0deg, rgba(255,255,255,0.028) 0 1px, transparent 1px 64px)",
        }}
      />

      <div className="relative w-full max-w-[394px]">
        <div className="mb-[22px] inline-flex items-center gap-2 rounded-md border border-[#B4770F] px-2.5 py-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-[#E2A33C]">
          <ShieldExclamationIcon className="h-3.5 w-3.5" />
          Operator access
        </div>

        <div className="mb-[26px] flex items-center gap-3.5">
          <div className="flex h-14 w-14 items-center justify-center rounded-[13px] bg-[#E7ECF1] text-xl font-extrabold text-[#0C1116]">
            CT
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="text-[19px] font-bold tracking-tight text-[#E7ECF1]">
              {stage === "credentials" ? "CITS Time" : "Verify it's you"}
            </div>
            <div className="font-mono text-[12px] tracking-wide text-[#8D99A5]">
              {stage === "credentials" ? "Platform operations" : email || "operator"}
              {stage === "totp" && (
                <button
                  type="button"
                  onClick={startOver}
                  className="ml-2 font-sans text-[12px] font-semibold text-[#E2A33C] hover:underline"
                >
                  {resumed ? "Start over" : "Not you?"}
                </button>
              )}
            </div>
          </div>
        </div>

        <form
          onSubmit={stage === "credentials" ? submitCredentials : submitTotp}
          className="flex flex-col gap-4"
        >
          {stage === "credentials" ? (
            <>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="op-email" className="text-[11px] font-semibold uppercase tracking-wider text-[#9AA6B2]">
                  Email
                </label>
                <input
                  id="op-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-[47px] rounded-[9px] border border-[#2A343E] bg-[#141B22] px-3.5 text-[14.5px] text-[#E7ECF1] outline-none transition focus:border-[#E2A33C] focus:ring-4 focus:ring-[#E2A33C]/20"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="op-pass" className="text-[11px] font-semibold uppercase tracking-wider text-[#9AA6B2]">
                  Password
                </label>
                <input
                  id="op-pass"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  ref={passwordRef}
                  className="h-[47px] rounded-[9px] border border-[#2A343E] bg-[#141B22] px-3.5 text-[14.5px] text-[#E7ECF1] outline-none transition focus:border-[#E2A33C] focus:ring-4 focus:ring-[#E2A33C]/20"
                />
              </div>
            </>
          ) : (
            <>
            {resumed && (
              <div
                className="flex items-start gap-2 rounded-[9px] border border-[#E2A33C]/30 bg-[#E2A33C]/[0.08] px-3 py-2.5 text-[12.5px] leading-snug text-[#D9B77B]"
                role="status"
              >
                <ArrowPathIcon className="mt-0.5 h-4 w-4 flex-none" />
                Picked up where you left off. Your password was already accepted.
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="op-totp" className="text-[11px] font-semibold uppercase tracking-wider text-[#9AA6B2]">
                Authenticator code
              </label>
              <input
                id="op-totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                ref={codeRef}
                className="h-[54px] rounded-[9px] border border-[#2A343E] bg-[#141B22] px-3 text-center font-mono text-[21px] font-semibold tracking-[0.4em] text-[#E7ECF1] outline-none transition focus:border-[#E2A33C] focus:ring-4 focus:ring-[#E2A33C]/20"
              />
              <p className="font-mono text-[11px] tabular-nums text-[#66717D]">
                Code screen open for {formatRemaining(left)}
              </p>
            </div>
            </>
          )}

          {expiredNotice && stage === "credentials" && (
            <div
              className="flex items-start gap-2 rounded-[9px] border border-[#E2A33C]/30 bg-[#E2A33C]/[0.08] px-3 py-2.5 text-[12.5px] leading-snug text-[#D9B77B]"
              role="status"
            >
              <ClockIcon className="mt-0.5 h-4 w-4 flex-none" />
              That sign-in timed out before the code was entered. Enter your password again to get a new
              one.
            </div>
          )}

          {error && (
            <div className="rounded-[9px] border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-[12.5px] text-red-300" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="flex h-[49px] items-center justify-center gap-2 rounded-[9px] bg-[#E7ECF1] text-[15px] font-bold text-[#0C1116] transition hover:bg-white disabled:opacity-60"
          >
            {busy ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <LockClosedIcon className="h-4 w-4" />}
            {stage === "credentials" ? "Sign in" : "Verify"}
          </button>

          {stage === "credentials" && passkeysReady() && (
            <>
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-[#2A343E]" />
                <span className="font-mono text-[10.5px] uppercase tracking-widest text-[#66717D]">or</span>
                <span className="h-px flex-1 bg-[#2A343E]" />
              </div>
              <button
                type="button"
                onClick={signInWithPasskey}
                disabled={busy}
                className="flex h-[46px] items-center justify-center gap-2.5 rounded-[9px] border border-[#2A343E] bg-[#141B22] text-[13.5px] font-semibold text-[#E7ECF1] transition hover:border-[#3A464F] disabled:opacity-60"
              >
                <KeyIcon className="h-4 w-4" />
                Use a passkey
              </button>
            </>
          )}

          <div className="mt-1 flex gap-2.5 rounded-[9px] border border-[#E2A33C]/30 bg-[#E2A33C]/[0.08] p-3.5">
            <ShieldExclamationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#D9B77B]" />
            <p className="text-[12.5px] leading-relaxed text-[#D9B77B]">
              <strong>Two-factor is mandatory here.</strong> This login reaches every tenant database, so there is no
              trust-this-device bypass and destructive actions re-prompt for recent authentication.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
